import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { AssetResponse } from '../models/asset.model';
import { HandoffResponse } from '../models/handoff.model';
import { Session, SessionInputPayload, SessionStatus } from '../models/session.model';
import { SessionsListResponse } from '../models/session-list.model';
import { ConversationResponse } from '../models/conversation.model';
import { PreviewsResponse } from '../models/previews.model';
import { DownloadUrlResponse } from '../models/download.model';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class SessionService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;

  listSessions(page: number, perPage: number, status?: SessionStatus): Observable<SessionsListResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('per_page', perPage.toString());

    if (status) {
      params = params.set('status', status);
    }

    return this.http.get<SessionsListResponse>(`${this.apiUrl}/api/sessions`, { params });
  }

  createSession(s3HandoffKey: string): Observable<Session> {
    return this.http.post<Session>(`${this.apiUrl}/api/sessions`, {
      s3_handoff_key: s3HandoffKey,
      deck_type: 'CAMPAIGN_PROPOSAL',
    });
  }

  getSession(id: string): Observable<Session> {
    return this.http.get<Session>(`${this.apiUrl}/api/sessions/${id}`);
  }

  getConversation(id: string): Observable<ConversationResponse> {
    return this.http.get<ConversationResponse>(`${this.apiUrl}/api/sessions/${id}/conversation`);
  }

  getPreviews(id: string): Observable<PreviewsResponse> {
    return this.http.get<PreviewsResponse>(`${this.apiUrl}/api/sessions/${id}/previews`);
  }

  submitInput(id: string, payload: SessionInputPayload): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/api/sessions/${id}/input`, payload);
  }

  uploadLogo(id: string, file: File): Observable<AssetResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<AssetResponse>(`${this.apiUrl}/api/sessions/${id}/logo`, formData);
  }

  getHandoff(id: string): Observable<HandoffResponse> {
    return this.http.get<HandoffResponse>(`${this.apiUrl}/api/sessions/${id}/handoff`);
  }

  /**
   * Fetch the presigned deck download URL through the authenticated HttpClient
   * path (the auth interceptor attaches the Cognito token) and open it in a new
   * tab. A bare `window.open` to the endpoint would bypass the interceptor and
   * be rejected by the BFF with a 401 (missing authorization header).
   */
  downloadDeck(id: string): Observable<DownloadUrlResponse> {
    return this.http
      .get<DownloadUrlResponse>(`${this.apiUrl}/api/sessions/${id}/deck`)
      .pipe(tap(({ url }) => window.open(url, '_blank')));
  }
}
